import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import UserAvatar from '../components/UserAvatar';
import { STATUS_LABELS, TYPE_META, StatusPill, TypeTile, isOverdue, taskTitle } from '../components/TaskBits';
import api, { formatDate, formatTime, relativeDay } from '../utils/api';
import { useEvents, useTasks, useTeamMembers } from '../lib/queries';
import { Button } from '../components/ui/button';
import AppSelect from '../components/ui/app-select';
import DateField from '../components/ui/date-field';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  CheckSquare,
  Clock3,
  Ellipsis,
  Eye,
  ListPlus,
  Plus,
  Search,
  Trash2,
  X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import SingleDateTimePicker from './SingleDateTimePicker';

import OtherWorkTypeInput from '../components/OtherWorkTypeInput';
import { workTypeLabel, workTypeOptions, workTypePayload } from '../utils/workTypes';

const time = (date) => new Date(date || 0).getTime() || 0;
const STANDALONE_EVENT_ID = '8d304940-none-none-a5c5-2c5d26d000c9';
const EMPTY_TASK = {
  type: 'photo',
  assigned_to: '',
  due_date: '',
  comments: '',
  event_id: STANDALONE_EVENT_ID
};

function DueTile({ date }) {
  if (!date) {
    return (
      <div className="agenda-date no-date" aria-hidden="true">
        <strong>--</strong>
        <span>No due</span>
      </div>
    );
  }
  const d = new Date(date);
  return (
    <div className={`agenda-date ${relativeDay(date) === 'Today' ? 'is-today' : ''}`} aria-hidden="true">
      <strong>{String(d.getDate()).padStart(2, '0')}</strong>
      <span>{d.toLocaleDateString('en-US', { weekday: 'short' })}</span>
    </div>
  );
}

const Tasks = () => {
  const { isAdmin, isMediaHead } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: tasks = [], isPending: loading, isError, error: loadError, refetch: refetchTasks } = useTasks();
  const { data: teamMembers = [] } = useTeamMembers();
  const { data: allEvents = [] } = useEvents();
  const [tab, setTab] = useState('all');
  const [type, setType] = useState('all');
  const [assignee, setAssignee] = useState('all');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTask, setNewTask] = useState({ ...EMPTY_TASK });



  // The sidebar "+" shortcut links here with ?create=new
  useEffect(() => {
    if (new URLSearchParams(location.search).get('create') === 'new') {
      if (isAdmin || isMediaHead) setTaskDialogOpen(true);
      navigate('/tasks', { replace: true });
    }
  }, [location.search, isAdmin, isMediaHead, navigate]);

  const formatError = (detail) => {
    if (Array.isArray(detail)) {
      return detail.map((item) => item.msg || JSON.stringify(item)).join(' | ');
    }
    if (typeof detail === 'string') return detail;
    return 'Please try again';
  };

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load tasks', {
        description: formatError(loadError?.response?.data?.detail)
      });
    }
  }, [isError, loadError]);

  const handleCreateTask = async () => {
    if (creating) return;
    if (!newTask.assigned_to || !newTask.due_date) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      setCreating(true);
      // The backend treats the placeholder id as "no event".
      const payload = workTypePayload({ ...newTask, event_id: newTask.event_id || STANDALONE_EVENT_ID });
      delete payload.due_date_obj;
      await api.post('/tasks', payload);
      toast.success('Task created successfully');
      setTaskDialogOpen(false);
      setNewTask({ ...EMPTY_TASK });
    } catch (error) {
      toast.error('Failed to create task', {
        description: formatError(error.response?.data?.detail)
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTask = async () => {
    if (deleting || !taskToDelete) return;
    try {
      setDeleting(true);
      await api.delete(`/tasks/${taskToDelete.id}`);
      toast.success('Task deleted successfully');
      setTaskToDelete(null);
      setConfirmOpen(false);
    } catch (error) {
      toast.error('Failed to delete task', {
        description: formatError(error.response?.data?.detail)
      });
    } finally {
      setDeleting(false);
    }
  };

  const assignees = useMemo(() => {
    const map = new Map();
    tasks.forEach((t) => {
      if (t.assigned_to) map.set(t.assigned_to, t.assigned_to_name || 'Unassigned');
    });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [tasks]);

  const counts = useMemo(
    () => ({
      all: tasks.length,
      assigned: tasks.filter((t) => t.status === 'assigned').length,
      in_progress: tasks.filter((t) => t.status === 'in_progress').length,
      completed: tasks.filter((t) => t.status === 'completed').length,
      overdue: tasks.filter(isOverdue).length
    }),
    [tasks]
  );

  const TABS = [
    { id: 'all', label: 'All' },
    { id: 'assigned', label: 'Assigned' },
    { id: 'in_progress', label: 'In progress' },
    { id: 'completed', label: 'Completed' },
    { id: 'overdue', label: 'Overdue', danger: true }
  ];

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const from = fromDate ? new Date(`${fromDate}T00:00:00`) : null;
    const to = toDate ? new Date(`${toDate}T23:59:59`) : null;
    return tasks
      .filter((t) => {
        if (tab === 'overdue' ? !isOverdue(t) : tab !== 'all' && t.status !== tab) return false;
        if (type !== 'all' && t.type !== type) return false;
        if (assignee !== 'all' && t.assigned_to !== assignee) return false;
        if (from || to) {
          const created = t.created_at ? new Date(t.created_at) : null;
          if (!created || (from && created < from) || (to && created > to)) return false;
        }
        if (query) {
          const haystack = `${taskTitle(t)} ${workTypeLabel(t.type)} ${t.assigned_to_name || ''} ${t.institution_name || ''} ${t.comments || ''}`;
          if (!haystack.toLowerCase().includes(query)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (!a.due_date && !b.due_date) return time(b.created_at) - time(a.created_at);
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return time(a.due_date) - time(b.due_date);
      });
  }, [tasks, tab, type, assignee, search, fromDate, toDate]);

  const groups = [];
  filtered.forEach((task) => {
    const key = task.due_date
      ? `${new Date(task.due_date).getFullYear()}-${new Date(task.due_date).getMonth()}`
      : 'none';
    let group = groups[groups.length - 1];
    if (!group || group.key !== key) {
      group = {
        key,
        label: task.due_date
          ? new Date(task.due_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
          : 'No due date',
        items: []
      };
      groups.push(group);
    }
    group.items.push(task);
  });

  const hasFilters =
    search || tab !== 'all' || type !== 'all' || assignee !== 'all' || fromDate || toDate;
  const clearFilters = () => {
    setTab('all');
    setType('all');
    setAssignee('all');
    setSearch('');
    setFromDate('');
    setToDate('');
  };
  const donePct = counts.all ? Math.round((counts.completed / counts.all) * 100) : 0;

  const renderMenu = (task) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="row-menu-trigger"
          aria-label={`Actions for ${taskTitle(task)}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Ellipsis size={18} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[150px]">
        <DropdownMenuItem onSelect={() => setSelectedTask(task)}>
          <Eye size={15} />
          View details
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-red-600 focus:text-red-700"
          onSelect={() => {
            setTaskToDelete(task);
            setConfirmOpen(true);
          }}
        >
          <Trash2 size={15} />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <Layout>
      <div className="dashboard-page institutions-page">
        <div className="dashboard-heading">
          <div>
            <p className="page-eyebrow">
              {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            <h1>Tasks</h1>
            <p>All work across events and standalone requests.</p>
          </div>
          {(isAdmin || isMediaHead) && (
            <div className="dashboard-heading-actions">
              <Button data-testid="create-task-button" onClick={() => setTaskDialogOpen(true)}>
                <Plus size={17} />
                New task
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="dashboard-loading" role="status">
            <div className="brand-spinner" />
            <h2>Loading tasks</h2>
            <p>Gathering the team's work.</p>
          </div>
        ) : (
          <>
            <section className="progress-strip" aria-label="Task progress">
              <div className="progress-copy">

                <div>
                  <strong>
                    {counts.completed} of {counts.all} tasks completed
                  </strong>
                  <p>
                    {counts.overdue
                      ? `${counts.overdue} overdue and need attention`
                      : 'Nothing is overdue right now'}
                  </p>
                </div>
              </div>
              <div className="progress-bar" role="img" aria-label={`${donePct}% complete`}>
                <span style={{ width: `${donePct}%` }} />
              </div>
              <div className="progress-stats">
                <span>
                  <i className="dot-grey" />
                  <strong>{counts.assigned}</strong> Assigned
                </span>
                <span>
                  <i className="dot-amber" />
                  <strong>{counts.in_progress}</strong> In progress
                </span>
                <span>
                  <i className="dot-green" />
                  <strong>{counts.completed}</strong> Done
                </span>
              </div>
            </section>

            <section className="dashboard-panel inst-panel" aria-labelledby="tasks-list-title">
              <h2 id="tasks-list-title" className="sr-only">
                All tasks
              </h2>
              <div className="events-toolbar-row">
                <div className="tab-bar" role="tablist" aria-label="Task status">
                  {TABS.map((t) => (
                    <button
                      key={t.id}
                      role="tab"
                      aria-selected={tab === t.id}
                      data-testid={`tab-${t.id}`}
                      className={`${tab === t.id ? 'is-active' : ''} ${t.danger && counts.overdue ? 'has-alert' : ''}`}
                      onClick={() => setTab(t.id)}
                    >
                      {t.label}
                      <span>{counts[t.id]}</span>
                    </button>
                  ))}
                </div>
                <div className="toolbar-controls">
                  <AppSelect
                    aria-label="Filter by type"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    options={[
                      { value: 'all', label: 'Any type' },
                      ...workTypeOptions(tasks.map(task => task.type))
                    ]}
                  />
                  <AppSelect
                    aria-label="Filter by assignee"
                    value={assignee}
                    onChange={(e) => setAssignee(e.target.value)}
                    options={[
                      { value: 'all', label: 'Everyone' },
                      ...assignees.map(([id, name]) => ({ value: id, label: name }))
                    ]}
                  />
                  <div className="date-range" title="Filter by the date a task was assigned">
                    <DateField
                      aria-label="Assigned from"
                      placeholder="From"
                      value={fromDate}
                      max={toDate || undefined}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                    <span>to</span>
                    <DateField
                      aria-label="Assigned to"
                      placeholder="To"
                      value={toDate}
                      min={fromDate || undefined}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                  <div className="event-search inst-search">
                    <Search size={18} aria-hidden="true" />
                    <input
                      aria-label="Search tasks"
                      type="search"
                      placeholder="Search tasks"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                      <button onClick={() => setSearch('')} aria-label="Clear search">
                        <X size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {filtered.length ? (
                <div className="agenda">
                  {groups.map((group) => (
                    <section key={group.key} className="agenda-group" aria-label={group.label}>
                      <h3 className="agenda-month">
                        {group.label}
                        <span className="count-pill">{group.items.length}</span>
                      </h3>
                      <ul className="agenda-list">
                        {group.items.map((task) => {
                          const overdue = isOverdue(task);
                          const rel = task.due_date ? relativeDay(task.due_date) : null;
                          return (
                            <li key={task.id}>
                              <article
                                className="agenda-item"
                                data-testid={`task-card-${task.id}`}
                                onClick={() => setSelectedTask(task)}
                              >
                                <DueTile date={task.due_date} />
                                <TypeTile type={task.type} />
                                <div className="agenda-body">
                                  <div className="agenda-title-row">
                                    <span className="agenda-title">{taskTitle(task)}</span>
                                  </div>
                                  <div className="agenda-meta">
                                    <span className="agenda-inst">
                                      {workTypeLabel(task.type)}
                                    </span>
                                    <span>
                                      <UserAvatar id={task.assigned_to} name={task.assigned_to_name} size={20} />
                                      {task.assigned_to_name || 'Unassigned'}
                                    </span>
                                    {task.due_date && (
                                      <span>
                                        <Clock3 size={14} />
                                        {formatTime(task.due_date)}
                                      </span>
                                    )}
                                    {task.institution_name && <span>{task.institution_name}</span>}
                                  </div>
                                  {task.comments && <p className="task-note">{task.comments}</p>}
                                </div>
                                <div className="agenda-side">
                                  <span
                                    className={`rel-day ${overdue ? 'is-overdue' : rel === 'Today' ? 'is-today' : ''}`}
                                  >
                                    {task.status === 'completed'
                                      ? task.created_at
                                        ? `Assigned ${formatDate(task.created_at)}`
                                        : ''
                                      : overdue
                                        ? `Overdue · ${rel}`
                                        : rel || 'No due date'}
                                  </span>
                                  <div className="agenda-pills">
                                    <StatusPill status={task.status} />
                                  </div>
                                </div>
                                <div className="agenda-menu">{renderMenu(task)}</div>
                              </article>
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  ))}
                </div>
              ) : (
                <div className="dashboard-empty">
                  <CheckSquare size={28} />
                  <h3>{hasFilters ? 'No matching tasks' : 'No tasks yet'}</h3>
                  <p>
                    {hasFilters
                      ? 'Try a different search or clear your filters.'
                      : 'Create a task to start assigning work.'}
                  </p>
                  {hasFilters ? (
                    <Button variant="outline" onClick={clearFilters}>
                      Clear filters
                    </Button>
                  ) : (
                    (isAdmin || isMediaHead) && (
                      <Button onClick={() => setTaskDialogOpen(true)}>New task</Button>
                    )
                  )}
                </div>
              )}

              <div className="table-footer">
                <span role="status" aria-live="polite">
                  {filtered.length === tasks.length
                    ? `${tasks.length} tasks`
                    : `${filtered.length} of ${tasks.length} tasks`}
                </span>
                <button className="footer-refresh" onClick={() => refetchTasks()}>
                  Refresh
                </button>
              </div>
            </section>
          </>
        )}
      </div>

      <Dialog open={!!selectedTask} onOpenChange={(open) => !open && setSelectedTask(null)}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Task details</DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <TypeTile type={selectedTask.type} size={44} />
                <div className="min-w-0">
                  <p className="text-lg font-semibold text-slate-900 leading-snug">
                    {taskTitle(selectedTask)}
                  </p>
                  <p className="text-sm text-slate-500">
                    {workTypeLabel(selectedTask.type)}
                    {selectedTask.institution_name ? ` · ${selectedTask.institution_name}` : ''}
                  </p>
                </div>
              </div>
              <StatusPill status={selectedTask.status} />
              <p className="text-sm text-slate-700">{selectedTask.comments || 'No description'}</p>
              <div className="grid grid-cols-3 gap-3 text-sm text-slate-700">
                <div>
                  <p className="text-slate-500">Assigned to</p>
                  <p>{selectedTask.assigned_to_name || '—'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Assigned on</p>
                  <p>{selectedTask.created_at ? formatDate(selectedTask.created_at) : '—'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Due</p>
                  <p>{selectedTask.due_date ? formatDate(selectedTask.due_date) : 'Not set'}</p>
                </div>
              </div>
              {selectedTask.deliverable_link && (
                <a
                  href={selectedTask.deliverable_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#37429c] hover:underline text-sm"
                >
                  View deliverable
                </a>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSelectedTask(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmOpen(false);
            setTaskToDelete(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Delete task</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-slate-700">Are you sure you want to delete this task?</p>
            <p className="text-sm font-semibold text-slate-900">
              {taskToDelete ? taskTitle(taskToDelete) : ''}
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setConfirmOpen(false);
                setTaskToDelete(null);
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeleteTask}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {(isAdmin || isMediaHead) && (
        <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
          <DialogContent className="sm:max-w-[680px] max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="sr-only">Create task</DialogTitle>
            </DialogHeader>
            <div className="sheet-head">
              <span className="sheet-icon">
                <ListPlus size={22} strokeWidth={1.7} />
              </span>
              <div>
                <h3>Create task</h3>
                <p>Assign a piece of work and set when it is due.</p>
              </div>
            </div>

            <form
              className="sheet-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleCreateTask();
              }}
            >
              <section className="form-section" aria-labelledby="task-type-title">
                <div className="form-section-head">
                  <h2 id="task-type-title">Type of work</h2>
                  <p>What the person will deliver.</p>
                </div>
                <div className="chip-radio" role="radiogroup" aria-labelledby="task-type-title">
                  {Object.entries(TYPE_META).map(([value, meta]) => {
                    const Icon = meta.icon;
                    const selected = newTask.type === value;
                    return (
                      <button
                        type="button"
                        key={value}
                        role="radio"
                        aria-checked={selected}
                        className={selected ? 'is-selected' : ''}
                        onClick={() => setNewTask({ ...newTask, type: value })}
                      >
                        <Icon size={16} strokeWidth={1.7} />
                        {meta.label}
                      </button>
                    );
                  })}
                </div>
                <OtherWorkTypeInput value={newTask} onChange={setNewTask} />
              </section>

              <section className="form-section" aria-labelledby="task-who-title">
                <div className="form-section-head">
                  <h2 id="task-who-title">Who and when</h2>
                  <p>Pick the owner and the deadline.</p>
                </div>
                <div className="field">
                  <label htmlFor="task-assignee">
                    Assign to <i className="req">*</i>
                  </label>
                  <AppSelect
                    id="task-assignee"
                    variant="field"
                    placeholder="Select team member"
                    value={newTask.assigned_to}
                    onChange={(e) => setNewTask({ ...newTask, assigned_to: e.target.value })}
                    options={[...teamMembers]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((member) => ({ value: member.id.toString(), label: `${member.name} (${member.email})` }))}
                  />
                </div>
                <div className="field">
                  <label>
                    Due date <i className="req">*</i>
                  </label>
                  <div className="picker-box">
                    <SingleDateTimePicker
                      label="Due date"
                      value={newTask.due_date}
                      onChange={(val) =>
                        setNewTask({
                          ...newTask,
                          due_date: val.value, // "YYYY-MM-DDTHH:mm"
                          due_date_obj: val.date
                        })
                      }
                    />
                  </div>
                </div>
              </section>

              <section className="form-section" aria-labelledby="task-details-title">
                <div className="form-section-head">
                  <h2 id="task-details-title">Details</h2>
                  <p>Link it to an event, or leave it standalone.</p>
                </div>
                <div className="field">
                  <label htmlFor="task-event">Linked event</label>
                  <AppSelect
                    id="task-event"
                    variant="field"
                    value={newTask.event_id}
                    onChange={(e) => setNewTask({ ...newTask, event_id: e.target.value })}
                    options={[
                      { value: STANDALONE_EVENT_ID, label: 'No event (standalone task)' },
                      ...allEvents.map((evt) => ({ value: evt.id.toString(), label: evt.title }))
                    ]}
                  />
                </div>
                <div className="field">
                  <label htmlFor="task-comments">Comments</label>
                  <textarea
                    id="task-comments"
                    className="field-input field-textarea"
                    rows={3}
                    value={newTask.comments}
                    onChange={(e) => setNewTask({ ...newTask, comments: e.target.value })}
                    placeholder="Give guidance or requirements for this task"
                  />
                </div>
              </section>

              <div className="form-actions">
                <Button type="button" variant="ghost" onClick={() => setTaskDialogOpen(false)} disabled={creating}>
                  Cancel
                </Button>
                <Button type="submit" disabled={creating}>
                  {creating ? 'Creating…' : 'Create task'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </Layout>
  );
};

export default Tasks;
